import { Button } from '@/components/Button/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { Check, ChevronDown } from 'lucide-react';
import type { EditorInstance } from 'novel';
import { useState } from 'react';

export interface BubbleColorMenuItem {
  name: string;
  color: string;
  borderColor: string;
}

export const BASE_HIGHLIGHT_COLORS: BubbleColorMenuItem[] = [
  {
    name: 'Orange',
    color: 'var(--highlight-orange)',
    borderColor: 'var(--highlight-orange-border)',
  },
  {
    name: 'Yellow',
    color: 'var(--highlight-yellow)',
    borderColor: 'var(--highlight-yellow-border)',
  },
  {
    name: 'Green',
    color: 'var(--highlight-green)',
    borderColor: 'var(--highlight-green-border)',
  },
  {
    name: 'Blue',
    color: 'var(--highlight-blue)',
    borderColor: 'var(--highlight-blue-border)',
  },
  {
    name: 'Purple',
    color: 'var(--highlight-purple)',
    borderColor: 'var(--highlight-purple-border)',
  },
  {
    name: 'Red',
    color: 'var(--highlight-red)',
    borderColor: 'var(--highlight-red-border)',
  },
];
const HIGHLIGHT_COLORS: BubbleColorMenuItem[] = [
  ...BASE_HIGHLIGHT_COLORS,
  {
    name: 'Default',
    color: 'var(--highlight-default)',
    borderColor: 'var(--highlight-default-border)',
  },
];

export const PdfColorSelector = ({
  currentColor,
  onColorChange,
}: {
  currentColor: string;
  onColorChange: (event: React.MouseEvent, color: BubbleColorMenuItem) => void;
}) => {
  const [openColor, setOpenColor] = useState(false);

  const activeHighlightItem = BASE_HIGHLIGHT_COLORS.find(
    ({ color }) => color === currentColor,
  );

  return (
    <Popover modal={true} open={openColor} onOpenChange={setOpenColor}>
      <PopoverTrigger asChild className="hover:bg-popover-hover h-[30px]">
        <Button size="xs" className="gap-1" variant="ghost">
          <span
            className="h-4 w-4 flex-shrink-0 rounded-full border"
            style={{
              backgroundColor: activeHighlightItem?.color,
              borderColor: activeHighlightItem?.borderColor,
            }}
          />
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="flex w-48 flex-col rounded-lg border p-0.5 shadow-xl "
        align="start"
      >
        {BASE_HIGHLIGHT_COLORS.map((colorItem) => (
          <div
            key={colorItem.name}
            onClick={(event: React.MouseEvent) => {
              onColorChange(event, colorItem);
              setOpenColor(false);
            }}
            className="flex cursor-pointer items-center justify-between px-1.5 py-1 rounded-md text-sm hover:bg-accent"
          >
            <div className="flex items-center gap-2">
              <span
                className={'h-4 w-4 flex-shrink-0 rounded-full border'}
                style={{
                  backgroundColor: colorItem.color,
                  borderColor: colorItem.borderColor,
                }}
              />

              <span>{colorItem.name}</span>
            </div>
            {colorItem.color === currentColor && (
              <Check className="h-4 w-4 text-badgeBlue" />
            )}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export const ColorSelector = ({ editor }: { editor: EditorInstance }) => {
  const [openColor, setOpenColor] = useState(false);

  if (!editor) return null;

  const activeHighlightItem = HIGHLIGHT_COLORS.find(({ color }) =>
    editor.isActive('highlight', { color }),
  );

  return (
    <Popover modal={true} open={openColor} onOpenChange={setOpenColor}>
      <PopoverTrigger asChild className="hover:bg-popover-hover">
        <Button size="xs" className="gap-1" variant="ghost">
          <span
            className="h-4 w-4 flex-shrink-0 rounded-full border"
            style={{
              backgroundColor: activeHighlightItem?.color ?? 'black',
              borderColor: activeHighlightItem?.borderColor ?? 'black',
            }}
          />
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="flex w-48 flex-col rounded-lg border p-0.5 shadow-xl "
        align="start"
      >
        {HIGHLIGHT_COLORS.map(({ name, color, borderColor }) => (
          <div
            key={name}
            onClick={() => {
              editor.commands.unsetHighlight();
              name !== 'Default' &&
                editor.chain().focus().setHighlight({ color }).run();

              editor.commands.setTextSelection({
                from: editor.state.selection.from,
                to: editor.state.selection.from,
              });

              setOpenColor(false);
            }}
            className="flex cursor-pointer items-center justify-between px-1.5 py-1 rounded-md text-sm hover:bg-accent"
          >
            <div className="flex items-center gap-2">
              <span
                className={'h-4 w-4 flex-shrink-0 rounded-full border'}
                style={{
                  backgroundColor: color,
                  borderColor: borderColor,
                  // backgroundColor: activeHighlightItem?.color,
                }}
              />

              <span>{name}</span>
            </div>
            {editor.isActive('highlight', { color }) && (
              <Check className="h-4 w-4" />
            )}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
};
